(() => {
    const SCROLL_STEP_RATIO = 0.5;
    const SCROLL_EDGE_EPSILON = 2;

    const MEDIA_TYPE_TO_SECTION = {
        youtube: 'video',
        wistia: 'video',
        matterport: 'matterport',
        photo: 'photos',
    };

    const getViewport = (scroller) => scroller.querySelector('.media-scroller-viewport');
    const getPrevArrow = (scroller) => scroller.querySelector('.media-scroller-arrow-prev');
    const getNextArrow = (scroller) => scroller.querySelector('.media-scroller-arrow-next');
    const getPagination = (scroller) => scroller.querySelector('.media-scroller-pagination');

    const getMaxScrollLeft = (viewport) => Math.max(0, viewport.scrollWidth - viewport.clientWidth);

    /**
     * Size Still Looking against the viewport:
     * - Short track (large + cluster fit in view): fill remaining width so gray runs to the right
     *   edge (height clamp on ultra-wide leaves extra space the CSS column width cannot cover).
     * - Long track: lock to 1/4 viewport; only apply a small grid nudge (~vw/clientWidth delta).
     */
    const syncStillLookingEndAlignment = (scroller) => {
        const viewport = getViewport(scroller);
        const track = viewport && viewport.querySelector('.media-scroller-track');
        const stillLooking = track && track.querySelector('.media-scroller-still-looking');
        if (!viewport || !track || !stillLooking) {
            return;
        }

        stillLooking.style.width = '';

        if (scroller.classList.contains('media-scroller-single-media')) {
            return;
        }

        const largeTile = track.querySelector('.media-scroller-tile-large');
        const cluster = track.querySelector('.media-scroller-tile-cluster');
        const smallTile = cluster && cluster.querySelector('.media-scroller-tile-small');
        if (!cluster || !smallTile) {
            return;
        }

        const trackGap = parseFloat(getComputedStyle(track).columnGap || getComputedStyle(track).gap) || 0;
        const largeW = largeTile ? largeTile.getBoundingClientRect().width : 0;
        const clusterW = cluster.getBoundingClientRect().width;
        const smallW = smallTile.getBoundingClientRect().width;
        const columnPitch = smallW + trackGap;
        const clientWidth = viewport.clientWidth;
        const quarterWidth = clientWidth * 0.25;
        // Gaps sit between large|cluster and cluster|still.
        const widthBeforeStill = largeW + trackGap + clusterW + trackGap;
        const remainingForStill = clientWidth - widthBeforeStill;
        // Only correct the usual ~vw vs clientWidth drift; ignore large remainders when height is clamped.
        const maxGridNudge = 20;

        if (remainingForStill > 0) {
            // Fill the leftover slice so there is no white gap on the right (ultra-wide height clamp).
            stillLooking.style.width = remainingForStill + 'px';
            return;
        }

        // Long scroller: Still Looking is 1/4 of the viewport.
        let stillTarget = quarterWidth;
        const cutIntoCluster = clusterW + trackGap + stillTarget - clientWidth;
        const pitchRemainder = columnPitch > 0 ? ((cutIntoCluster % columnPitch) + columnPitch) % columnPitch : 0;
        if (pitchRemainder >= 0.5 && pitchRemainder <= maxGridNudge) {
            stillTarget -= pitchRemainder;
        }
        stillLooking.style.width = stillTarget + 'px';
    };

    const getPopupId = (scroller) => {
        const floorplanId = scroller.getAttribute('data-floorplan-id');
        if (!floorplanId) {
            return null;
        }
        return 'floorplan-' + floorplanId + '-vc-popup';
    };

    const openMediaPopup = (scroller, options) => {
        const popupId = getPopupId(scroller);
        if (!popupId || typeof openFloorplanVcPopupMagnific !== 'function') {
            return;
        }
        openFloorplanVcPopupMagnific(popupId, options);
    };

    const updateArrowState = (scroller) => {
        const viewport = getViewport(scroller);
        const prevArrow = getPrevArrow(scroller);
        const nextArrow = getNextArrow(scroller);
        if (!viewport || !prevArrow || !nextArrow) {
            return;
        }

        const scrollLeft = viewport.scrollLeft;
        const maxScrollLeft = getMaxScrollLeft(viewport);
        if (maxScrollLeft <= SCROLL_EDGE_EPSILON) {
            prevArrow.hidden = true;
            nextArrow.hidden = true;
            return;
        }

        prevArrow.hidden = false;
        nextArrow.hidden = false;

        const atStart = scrollLeft <= SCROLL_EDGE_EPSILON;
        const atEnd = scrollLeft >= maxScrollLeft - SCROLL_EDGE_EPSILON;

        prevArrow.disabled = atStart;
        nextArrow.disabled = atEnd;
    };

    /** Scroll offset of a track child relative to the viewport's scrollable content. */
    const getItemScrollLeft = (viewport, item) => {
        const viewportRect = viewport.getBoundingClientRect();
        const itemRect = item.getBoundingClientRect();
        return viewport.scrollLeft + (itemRect.left - viewportRect.left);
    };

    /**
     * Snap targets: large tile, each cluster column (2 stacked smalls), still-looking.
     * The cluster is one track child, so column lefts must be expanded or mid-photos are skipped.
     */
    const getSnapOffsets = (viewport, track) => {
        const offsets = [];
        const addOffset = (value) => {
            if (!offsets.some((existing) => Math.abs(existing - value) < 1)) {
                offsets.push(value);
            }
        };

        Array.from(track.children).forEach((item) => {
            if (item.classList.contains('media-scroller-tile-cluster')) {
                Array.from(item.querySelectorAll('.media-scroller-tile-small')).forEach((tile) => {
                    addOffset(getItemScrollLeft(viewport, tile));
                });
                return;
            }
            addOffset(getItemScrollLeft(viewport, item));
        });

        offsets.sort((a, b) => a - b);
        return offsets;
    };

    /**
     * Next scrollLeft from an arbitrary position using the same half-viewport + nearest-snap
     * rule as the arrow buttons.
     */
    const getSnapTargetOffset = (viewport, track, scrollLeft, direction) => {
        const epsilon = 1;
        const halfViewport = viewport.clientWidth * SCROLL_STEP_RATIO;
        const idealTarget = scrollLeft + direction * halfViewport;
        const maxScrollLeft = getMaxScrollLeft(viewport);
        const offsets = getSnapOffsets(viewport, track);

        let bestOffset = null;
        let bestDistance = Infinity;
        for (let i = 0; i < offsets.length; i++) {
            const offset = offsets[i];
            if (direction > 0 && offset <= scrollLeft + epsilon) {
                continue;
            }
            if (direction < 0 && offset >= scrollLeft - epsilon) {
                continue;
            }
            const distance = Math.abs(offset - idealTarget);
            if (distance < bestDistance) {
                bestDistance = distance;
                bestOffset = offset;
            }
        }

        if (bestOffset == null) {
            return direction > 0 ? maxScrollLeft : 0;
        }

        return Math.max(0, Math.min(bestOffset, maxScrollLeft));
    };

    /**
     * Pagination stops = positions visited by repeatedly stepping Next from the start
     * (half viewport, snapped). Matches arrow navigation rather than every column edge.
     */
    const getArrowStepOffsets = (viewport, track) => {
        const maxScrollLeft = getMaxScrollLeft(viewport);
        if (maxScrollLeft <= SCROLL_EDGE_EPSILON) {
            return [0];
        }

        const steps = [0];
        let scrollLeft = 0;
        const maxIterations = 64;
        for (let i = 0; i < maxIterations; i++) {
            if (scrollLeft >= maxScrollLeft - SCROLL_EDGE_EPSILON) {
                break;
            }
            const next = getSnapTargetOffset(viewport, track, scrollLeft, 1);
            if (next <= scrollLeft + SCROLL_EDGE_EPSILON) {
                if (Math.abs(steps[steps.length - 1] - maxScrollLeft) > 1) {
                    steps.push(maxScrollLeft);
                }
                break;
            }
            steps.push(next);
            scrollLeft = next;
        }

        if (Math.abs(steps[steps.length - 1] - maxScrollLeft) > 1) {
            steps.push(maxScrollLeft);
        }
        return steps;
    };

    const getNearestSnapIndex = (scrollLeft, offsets) => {
        let bestIndex = 0;
        let bestDistance = Infinity;
        for (let i = 0; i < offsets.length; i++) {
            const distance = Math.abs(offsets[i] - scrollLeft);
            if (distance < bestDistance) {
                bestDistance = distance;
                bestIndex = i;
            }
        }
        return bestIndex;
    };

    const getPaginationDotLabel = (index, offsetsLength) => {
        return `${index + 1} of ${offsetsLength}`;
    };

    const setPaginationActiveIndex = (pagination, activeIndex) => {
        const dots = pagination.querySelectorAll('.media-scroller-pagination-dot');
        dots.forEach((dot, index) => {
            const isActive = index === activeIndex;
            dot.classList.toggle('media-scroller-pagination-dot-active', isActive);
            dot.setAttribute('aria-selected', String(isActive));
            dot.tabIndex = isActive ? 0 : -1;
        });
        return dots[activeIndex] || null;
    };

    const updatePaginationActive = (scroller) => {
        const viewport = getViewport(scroller);
        const pagination = getPagination(scroller);
        if (!viewport || !pagination || pagination.hidden) {
            return;
        }

        const track = viewport.querySelector('.media-scroller-track');
        if (!track) {
            return;
        }

        const offsets = getArrowStepOffsets(viewport, track);
        const dots = pagination.querySelectorAll('.media-scroller-pagination-dot');
        if (!offsets.length || dots.length !== offsets.length) {
            return;
        }

        // While a dot has focus (keyboard/click), keep active with focus so smooth-scroll
        // intermediates do not move the outline or aria-selected away from the target.
        const focusedDot =
            document.activeElement instanceof Element &&
            document.activeElement.classList.contains('media-scroller-pagination-dot') &&
            pagination.contains(document.activeElement)
                ? document.activeElement
                : null;
        if (focusedDot) {
            const focusedIndex = Array.prototype.indexOf.call(dots, focusedDot);
            if (focusedIndex >= 0) {
                setPaginationActiveIndex(pagination, focusedIndex);
                return;
            }
        }

        setPaginationActiveIndex(pagination, getNearestSnapIndex(viewport.scrollLeft, offsets));
    };

    const activatePaginationIndex = (scroller, index, { moveFocus } = {}) => {
        const viewport = getViewport(scroller);
        const pagination = getPagination(scroller);
        if (!viewport || !pagination || pagination.hidden) {
            return;
        }

        const track = viewport.querySelector('.media-scroller-track');
        if (!track) {
            return;
        }

        const offsets = getArrowStepOffsets(viewport, track);
        if (index < 0 || index >= offsets.length) {
            return;
        }

        const activeDot = setPaginationActiveIndex(pagination, index);
        viewport.scrollTo({
            left: offsets[index],
            behavior: 'smooth',
        });
        if (moveFocus && activeDot && document.activeElement !== activeDot) {
            activeDot.focus({ preventScroll: true });
        }
    };

    const rebuildPagination = (scroller) => {
        const viewport = getViewport(scroller);
        const pagination = getPagination(scroller);
        if (!viewport || !pagination) {
            return;
        }

        const track = viewport.querySelector('.media-scroller-track');
        if (!track) {
            pagination.hidden = true;
            pagination.replaceChildren();
            return;
        }

        const maxScrollLeft = getMaxScrollLeft(viewport);
        if (maxScrollLeft <= SCROLL_EDGE_EPSILON) {
            pagination.hidden = true;
            pagination.replaceChildren();
            return;
        }

        const offsets = getArrowStepOffsets(viewport, track);
        if (offsets.length < 2) {
            pagination.hidden = true;
            pagination.replaceChildren();
            return;
        }

        const existingDots = pagination.querySelectorAll('.media-scroller-pagination-dot');
        if (existingDots.length !== offsets.length) {
            pagination.replaceChildren();
            offsets.forEach((_, index) => {
                const dot = document.createElement('button');
                dot.type = 'button';
                dot.className = 'media-scroller-pagination-dot';
                dot.setAttribute('role', 'tab');
                dot.setAttribute('aria-label', getPaginationDotLabel(index, offsets.length));
                dot.addEventListener('click', () => {
                    activatePaginationIndex(scroller, index);
                });
                pagination.appendChild(dot);
            });
        }

        pagination.hidden = false;
        updatePaginationActive(scroller);
    };

    /**
     * Distance toward the snap offset nearest a half-viewport step.
     * Avoids raw half-viewport math (vw-based tile height ≠ 0.5 * clientWidth), which drifted ~7px/step.
     */
    const getSnapScrollDistance = (viewport, direction) => {
        const track = viewport.querySelector('.media-scroller-track');
        if (!track) {
            return viewport.clientWidth * SCROLL_STEP_RATIO;
        }

        const scrollLeft = viewport.scrollLeft;
        const target = getSnapTargetOffset(viewport, track, scrollLeft, direction);
        return Math.abs(target - scrollLeft);
    };

    const scrollByStep = (scroller, direction) => {
        const viewport = getViewport(scroller);
        if (!viewport) {
            return;
        }

        const step = getSnapScrollDistance(viewport, direction);
        if (step <= 0) {
            return;
        }
        viewport.scrollBy({
            left: direction * step,
            behavior: 'smooth',
        });
    };

    const handleTileActivation = (scroller, tile) => {
        const mediaType = tile.getAttribute('data-media-type');
        const section = MEDIA_TYPE_TO_SECTION[mediaType];
        if (!section) {
            return;
        }

        const options = { section };
        if (section === 'photos') {
            const photoId = tile.getAttribute('data-media-index');
            if (photoId != null && photoId !== '') {
                options.photoId = photoId;
            }
        }

        openMediaPopup(scroller, options);
    };

    const maybeOpenFromQuery = (scroller) => {
        const vcParam = new URLSearchParams(window.location.search).get('vc');
        if (vcParam !== '1') {
            return;
        }

        const hasVideoTile = scroller.querySelector(
            '.media-scroller-tile[data-media-type="youtube"], .media-scroller-tile[data-media-type="wistia"]',
        );
        const hasMatterportTile = scroller.querySelector('.media-scroller-tile[data-media-type="matterport"]');
        if (!hasVideoTile && !hasMatterportTile) {
            return;
        }

        // Wait for window load so vc-popup / wistia scripts (late in the page) are ready.
        const openVideoPopup = () => openMediaPopup(scroller, { section: hasVideoTile ? 'video' : 'matterport' });
        if (document.readyState === 'complete') {
            openVideoPopup();
        } else {
            window.addEventListener('load', openVideoPopup);
        }
    };

    const initMediaScroller = (scroller) => {
        const viewport = getViewport(scroller);
        const prevArrow = getPrevArrow(scroller);
        const nextArrow = getNextArrow(scroller);
        if (!viewport || !prevArrow || !nextArrow) {
            return;
        }

        prevArrow.addEventListener('click', () => {
            if (prevArrow.disabled) {
                return;
            }
            scrollByStep(scroller, -1);
        });

        nextArrow.addEventListener('click', () => {
            if (nextArrow.disabled) {
                return;
            }
            scrollByStep(scroller, 1);
        });

        scroller.addEventListener('keydown', (event) => {
            if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') {
                return;
            }

            const { target } = event;
            if (!(target instanceof Element) || !scroller.contains(target)) {
                return;
            }

            // Tablist: Left/Right move focus with the active pagination dot.
            const paginationDot = target.closest('.media-scroller-pagination-dot');
            if (paginationDot) {
                const pagination = getPagination(scroller);
                const dots = pagination
                    ? Array.from(pagination.querySelectorAll('.media-scroller-pagination-dot'))
                    : [];
                const currentIndex = dots.indexOf(paginationDot);
                if (currentIndex < 0) {
                    return;
                }
                const nextIndex =
                    event.key === 'ArrowLeft'
                        ? Math.max(0, currentIndex - 1)
                        : Math.min(dots.length - 1, currentIndex + 1);
                event.preventDefault();
                if (nextIndex !== currentIndex) {
                    activatePaginationIndex(scroller, nextIndex, { moveFocus: true });
                }
                return;
            }

            // Let native button/link activation handle Enter/Space on tiles.
            // Arrow keys scroll the track when focus is inside the scroller.
            event.preventDefault();
            scrollByStep(scroller, event.key === 'ArrowLeft' ? -1 : 1);
        });

        scroller.addEventListener('click', (event) => {
            const { target } = event;
            if (!(target instanceof Element)) {
                return;
            }

            const tile = target.closest('.media-scroller-tile');
            if (!tile || !scroller.contains(tile)) {
                return;
            }

            event.preventDefault();
            handleTileActivation(scroller, tile);
        });

        viewport.addEventListener('scroll', () => {
            updateArrowState(scroller);
            updatePaginationActive(scroller);
        });

        window.addEventListener('resize', () => {
            syncStillLookingEndAlignment(scroller);
            updateArrowState(scroller);
            rebuildPagination(scroller);
        });

        syncStillLookingEndAlignment(scroller);
        updateArrowState(scroller);
        rebuildPagination(scroller);
        maybeOpenFromQuery(scroller);
    };

    const initMediaScrollers = () => {
        document.querySelectorAll('.media-scroller').forEach(initMediaScroller);
    };

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', initMediaScrollers);
    } else {
        initMediaScrollers();
    }
})();
